import { useCallback, useEffect, useRef, useState } from "react";
import { AgentDetail } from "@/pages/tenant/AgentDetail";

/**
 * Agent settings as a right-hand slide-over.
 *
 * Opening ONE agent's settings shouldn't take over the screen with the full
 * tenant-settings navigation — this drawer shows just the agent's config
 * (the same embedded AgentDetail the settings dialog uses) sliding in from
 * the right, resizable by dragging its left edge. Tenant/account settings
 * keep the full dialog.
 */
const MIN_WIDTH = 480;
const WIDTH_KEY = "agent-settings-drawer-width";

function maxWidth(): number {
  return Math.min(1200, Math.floor(window.innerWidth * 0.9));
}

function initialWidth(): number {
  const saved = Number(window.localStorage.getItem(WIDTH_KEY));
  const fallback = Math.min(760, maxWidth());
  const base = Number.isFinite(saved) && saved > 0 ? saved : fallback;
  return Math.max(MIN_WIDTH, Math.min(maxWidth(), base));
}

export function AgentSettingsDrawer({
  agentId,
  onClose,
  onOpenAgent,
  onCreateSubagent,
}: {
  agentId: string | null;
  onClose: () => void;
  /** Swap the drawer to another agent, e.g. a sub-agent. */
  onOpenAgent?: (agentId: string) => void;
  /** Open the sub-agent wizard. It lives in the settings dialog, like the New agent wizard. */
  onCreateSubagent?: (parentAgentId: string) => void;
}) {
  const [width, setWidth] = useState<number>(() => initialWidth());
  const draggingRef = useRef(false);

  // Close on Escape.
  useEffect(() => {
    if (!agentId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [agentId, onClose]);

  const onHandlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    const onMove = (ev: PointerEvent) => {
      if (!draggingRef.current) return;
      const next = Math.max(MIN_WIDTH, Math.min(maxWidth(), window.innerWidth - ev.clientX));
      setWidth(next);
    };
    const onUp = () => {
      draggingRef.current = false;
      setWidth((w) => {
        window.localStorage.setItem(WIDTH_KEY, String(w));
        return w;
      });
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, []);

  if (!agentId) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop — click to dismiss */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* Drawer */}
      <div
        className="absolute inset-y-0 right-0 flex flex-col border-l border-border bg-background shadow-2xl"
        style={{ width }}
        role="dialog"
        aria-label="Agent settings"
      >
        {/* Drag handle on the left edge */}
        <div
          className="group absolute inset-y-0 left-0 z-10 w-2 -translate-x-1 cursor-col-resize"
          onPointerDown={onHandlePointerDown}
          title="Drag to resize"
        >
          <div className="mx-auto h-full w-[3px] bg-transparent transition-colors group-hover:bg-primary/40" />
        </div>

        {/* No explicit close button — click the backdrop or press Escape to
            dismiss. The empty header bar was removed with the X so the agent
            settings start flush at the top. */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          <AgentDetail
            key={agentId}
            agentId={agentId}
            embedded
            onOpenAgent={onOpenAgent}
            onCreateSubagent={onCreateSubagent}
          />
        </div>
      </div>
    </div>
  );
}
